import { Action } from 'redux';
import { put, select } from '@redux-saga/core/effects';

import { CheckoutActions, CheckoutActionTypes } from '../checkout.types';
import { ACTION_CREATOR_NAME, ExponeaCheckoutSteps, analyticsHelper, errorToBasketErrorModel, paymentHelper } from 'helpers';
import { ErrorAction, AppState } from '../../root.types';
import { initCheckoutPageSaga } from './initCheckoutPage';
import { BasketDetailsModel } from 'models/Basket';
import { PaymentTypes } from 'models/Payment';
import { api } from 'repositories/api';
import { validateBasketReservationsSaga } from 'store/basket/actions';
import { BasketActionTypes } from 'store/basket';
import { BasketErrorModel } from 'models/Errors';
import { callBasketSaga, BasketSagaReturnType } from 'store/sagaHelpers';
import { loadDeliveryDetailsSaga } from './loadDeliveryDetails';

const RESERVATION_ERROR_MESSAGE = 'Reservation has expired (init pay by credit card or PayPal)';
const FINANCE_APPLICATION_ERROR_MESSAGE = 'Finance application in Redux store is not marked as filled. Looks like page was reloaded after form completed.';

export interface InitPaymentPageAction extends Action<CheckoutActionTypes.INIT_PAYMENT_PAGE> {
}

export interface InitPaymentPageActionStart extends Action<CheckoutActionTypes.INIT_PAYMENT_PAGE_START> {
}

export interface InitPaymentPageActionSuccess extends Action<CheckoutActionTypes.INIT_PAYMENT_PAGE_SUCCESS> {
}

export interface InitPaymentPageActionError extends ErrorAction<CheckoutActionTypes.INIT_PAYMENT_PAGE_ERROR> {
}

export const initPaymentPage: (currentLocation?: string) => InitPaymentPageAction = (currentLocation?: string) => ({
    type: CheckoutActionTypes.INIT_PAYMENT_PAGE,
    currentLocation
});
initPaymentPage[ACTION_CREATOR_NAME] = 'initPaymentPage';

export type InitPaymentPageFunc = (...args: Parameters<typeof initPaymentPage>) => void;

export function* initPaymentPageSaga(): BasketSagaReturnType {
    yield put<CheckoutActions>({
        type: CheckoutActionTypes.INIT_PAYMENT_PAGE_START
    });

    try {
        yield callBasketSaga(initCheckoutPageSaga);

        yield callBasketSaga(validateBasketReservationsSaga, {
            type: BasketActionTypes.VALIDATE_BASKET_RESERVATIONS,
            isBasketPage: false
        });

        const state: AppState = yield select();
        const paymentType = state.paymentState.paymentDetails.paymentType;

        if (state.basketState.shouldShowReservationWarning) {
            throw new BasketErrorModel(RESERVATION_ERROR_MESSAGE, undefined, 'initPaymentPageSaga');
        }

        if (state.basketState.basket && paymentType !== PaymentTypes.None) {
            sendAnalytics(state.basketState.basket, paymentType);
        }

        if (paymentHelper.isCreditRelatedPaymentType(paymentType) && !state.checkoutState.isIfcApplicationFilledByCustomer) {
            yield put<CheckoutActions>({
                type: CheckoutActionTypes.SET_SHOW_FINANCE_APPLICATION_NOT_VALID_WARNING,
                shouldShowFinanceApplicationNotValidWarning: true
            });
            throw new BasketErrorModel(FINANCE_APPLICATION_ERROR_MESSAGE, undefined, 'initPaymentPageSaga');
        }

        if (!state.checkoutState.deliveryDetails) {
            yield callBasketSaga(loadDeliveryDetailsSaga);
        }

        yield put<CheckoutActions>({
            type: CheckoutActionTypes.INIT_PAYMENT_PAGE_SUCCESS
        });

        return true;
    } catch (error) {
        yield put<CheckoutActions>({
            type: CheckoutActionTypes.INIT_PAYMENT_PAGE_ERROR,
            error: errorToBasketErrorModel(error, 'initPaymentPageSaga')
        });

        return false;
    }
}

function sendAnalytics(basket: BasketDetailsModel, paymentType: PaymentTypes): void {
    analyticsHelper.withLogging(() => {
        api.exponea.checkoutStage(ExponeaCheckoutSteps.Payment, basket, paymentType);
    }, 'initPaymentPageSaga');
}